import { useEffect, useId, type ReactNode } from 'react';

export function Page({
  title,
  subtitle,
  actions,
  children,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>{title}</h1>
          {subtitle && <p>{subtitle}</p>}
        </div>
        {actions && <div className="actions">{actions}</div>}
      </div>
      <div className="stack">{children}</div>
    </div>
  );
}

export function Card({
  title,
  actions,
  children,
  flush,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  flush?: boolean;
}) {
  return (
    <section className={`card${flush ? ' flush' : ''}`}>
      {(title || actions) && (
        <header>
          {title && <h2>{title}</h2>}
          {actions && <div className="actions">{actions}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

// App Bridge's types add a `variant` attribute to <button>; ours replaces it.
type BtnProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'variant'> & {
  variant?: 'primary' | 'critical' | 'plain' | 'default';
  size?: 'small';
  loading?: boolean;
};

export function Button({ variant = 'default', size, loading, children, className, disabled, ...rest }: BtnProps) {
  return (
    <button
      type="button"
      className={['btn', variant !== 'default' && variant, size, className].filter(Boolean).join(' ')}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? <span className="spinner" style={{ width: 14, height: 14 }} /> : null}
      {children}
    </button>
  );
}

export function Field({
  label,
  help,
  error,
  children,
}: {
  label?: ReactNode;
  help?: ReactNode;
  error?: string | null;
  children: (id: string) => ReactNode;
}) {
  const id = useId();
  return (
    <div className="field">
      {label && <label htmlFor={id}>{label}</label>}
      {children(id)}
      {help && <span className="help">{help}</span>}
      {error && <span className="error">{error}</span>}
    </div>
  );
}

export function TextField({
  label,
  value,
  onChange,
  help,
  multiline,
  type = 'text',
  placeholder,
  error,
  min,
  max,
  step,
}: {
  label?: ReactNode;
  value: string | number | null | undefined;
  onChange: (v: string) => void;
  help?: ReactNode;
  multiline?: boolean;
  type?: string;
  placeholder?: string;
  error?: string | null;
  min?: number;
  max?: number;
  step?: number;
}) {
  return (
    <Field label={label} help={help} error={error}>
      {(id) =>
        multiline ? (
          <textarea id={id} className="textarea" value={value ?? ''} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
        ) : (
          <input
            id={id}
            className="input"
            type={type}
            value={value ?? ''}
            placeholder={placeholder}
            min={min}
            max={max}
            step={step}
            onChange={(e) => onChange(e.target.value)}
          />
        )
      }
    </Field>
  );
}

export function NumberField(props: {
  label?: ReactNode;
  value: number | null | undefined;
  onChange: (v: number) => void;
  help?: ReactNode;
  min?: number;
  max?: number;
  step?: number;
}) {
  return (
    <TextField
      {...props}
      type="number"
      value={props.value ?? ''}
      onChange={(v) => props.onChange(v === '' ? 0 : Number(v))}
    />
  );
}

export function Select<T extends string>({
  label,
  value,
  options,
  onChange,
  help,
}: {
  label?: ReactNode;
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  help?: ReactNode;
}) {
  return (
    <Field label={label} help={help}>
      {(id) => (
        <select id={id} className="select" value={value} onChange={(e) => onChange(e.target.value as T)}>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
}

export function Toggle({
  label,
  checked,
  onChange,
  help,
  disabled,
}: {
  label: ReactNode;
  checked: boolean;
  onChange: (v: boolean) => void;
  help?: ReactNode;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div className="stack tight">
        <label htmlFor={id} className="label">
          {label}
        </label>
        {help && <span className="muted small">{help}</span>}
      </div>
      <label className="switch">
        <input id={id} type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
        <span />
      </label>
    </div>
  );
}

export function ColorField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <Field label={label}>
      {(id) => (
        <div className="color-field">
          <input type="color" value={value} onChange={(e) => onChange(e.target.value.toUpperCase())} aria-label={label} />
          <input id={id} className="input mono" value={value} onChange={(e) => onChange(e.target.value)} maxLength={7} />
        </div>
      )}
    </Field>
  );
}

export function Segmented<T extends string>({ value, options, onChange }: { value: T; options: { value: T; label: ReactNode }[]; onChange: (v: T) => void }) {
  return (
    <div className="segmented" role="radiogroup">
      {options.map((o) => (
        <button key={o.value} role="radio" aria-checked={o.value === value} className={o.value === value ? 'on' : ''} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Badge({ tone, children }: { tone?: 'success' | 'warning' | 'critical' | 'info' | 'brand'; children: ReactNode }) {
  return <span className={`badge ${tone ?? ''}`}>{children}</span>;
}

export function Banner({ tone, title, children }: { tone?: 'warning' | 'critical' | 'success'; title?: ReactNode; children?: ReactNode }) {
  return (
    <div className={`banner ${tone ?? ''}`} role={tone === 'critical' ? 'alert' : 'status'}>
      <div className="stack tight">
        {title && <strong>{title}</strong>}
        {children}
      </div>
    </div>
  );
}

export function Tabs<T extends string>({ value, tabs, onChange }: { value: T; tabs: { id: T; label: ReactNode }[]; onChange: (v: T) => void }) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((t) => (
        <button key={t.id} role="tab" aria-selected={t.id === value} className={t.id === value ? 'on' : ''} onClick={() => onChange(t.id)}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function Modal({
  title,
  open,
  onClose,
  children,
  footer,
}: {
  title: ReactNode;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true">
        <header>
          <h2>{title}</h2>
          <Button variant="plain" aria-label="Close" onClick={onClose}>
            ✕
          </Button>
        </header>
        <div className="body">{children}</div>
        {footer && <footer>{footer}</footer>}
      </div>
    </div>
  );
}

export function Spinner() {
  return (
    <div className="center">
      <div className="spinner" />
    </div>
  );
}

export function EmptyState({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <h3>{title}</h3>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

export function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: ReactNode }) {
  return (
    <div className="card stat">
      <span className="label">{label}</span>
      <span className="value">{value}</span>
      {hint && <span className="hint">{hint}</span>}
    </div>
  );
}

export function ErrorBanner({ error }: { error: unknown }) {
  if (!error) return null;
  return <Banner tone="critical" title="Something went wrong">{(error as Error).message}</Banner>;
}
